import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { Button, Input, type InputRef, Modal } from 'antd';
import {
  type CSSProperties,
  type FC,
  type KeyboardEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentUser } from 'src/lib/customHooks/useCurrentUser';
import { useHotKeyListener } from 'src/lib/customHooks/useHotKeyListener';
import { useIsAdmin } from 'src/lib/customHooks/useIsAdmin';
import { Platforms, getPlatformOfUserAgent } from 'src/lib/userAgent';
import { useOnClickOutside } from 'usehooks-ts';
import { KeyCap } from '../BasicComponents/Hotkeys';
import { IconSearch } from '../BasicComponents/Icons';
import { SearchPanel, opensInNewTab, optionId } from './SearchPanel';
import { useRecentSearches } from './useRecentSearches';
import { SearchCategory, type SearchItem, useSearchResults } from './useSearchResults';

interface Props {
  /** Header is in its collapsed layout: show a trigger that takes over the screen. */
  compact?: boolean;
}

export const GlobalSearch: FC<Props> = ({ compact = false }) => {
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const isAdmin = useIsAdmin();
  const listboxId = useId();
  const inputRef = useRef<InputRef>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [category, setCategory] = useState<SearchCategory>(SearchCategory.All);
  const [isOpen, setIsOpen] = useState(false);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  // Autofill skips a readonly field, and nothing else reliably keeps a password manager off it.
  // The header outlives route changes, so the field re-arms on blur rather than only on mount.
  const [isReadOnly, setIsReadOnly] = useState(true);
  const isMac = getPlatformOfUserAgent(navigator.userAgent) === Platforms.MAC;

  const {
    groups,
    items,
    tabs,
    activeCategory,
    debouncedQuery,
    isActive,
    isLoading,
    hasError,
    retry,
    hasNextPage,
    loadNextPage,
  } = useSearchResults(query, category);
  const { rememberSearch } = useRecentSearches(currentUser?.id ?? null);

  const isPanelOpen = isOpen && (isActive || items.length > 0);
  const activeOptionId =
    isPanelOpen && items.length > 0 ? optionId(listboxId, activeIndex) : undefined;

  // Never promises a category this user cannot search.
  const searchScope = isAdmin ? 'Search events, pages and users' : 'Search events and pages';

  const selectCategory = (next: SearchCategory) => {
    setCategory(next);
    setActiveIndex(0);
  };

  const focusInput = () => inputRef.current?.focus({ cursor: 'end' });
  const open = () => (compact ? setIsOverlayOpen(true) : focusInput());

  // useHotKeyListener rejects a modifier it was not asked for, so ⌘K and Ctrl+K need one each.
  useHotKeyListener('k', open, true);
  useHotKeyListener('k', open, false, false, true);

  useEffect(() => {
    setActiveIndex(0);
    // Empty stays closed: opening a result clears the field, which must not re-open the panel.
    // The category is sticky while a query is refined, so one letter does not leave the Users tab.
    if (query === '') setCategory(SearchCategory.All);
    else setIsOpen(true);
  }, [query]);

  useOnClickOutside(containerRef, () => setIsOpen(false));

  const closeOverlay = () => {
    setIsOverlayOpen(false);
    setIsOpen(false);
    setQuery('');
  };

  const handleSelect = (item: SearchItem, inNewTab = false) => {
    if (item.kind === 'recent') {
      setQuery(item.query);
      focusInput();
      return;
    }

    // Not on every search: a query nobody acted on is not worth offering.
    rememberSearch(debouncedQuery);

    // The point of a new tab is opening several, so the query and the panel stay as they are.
    // No feature string: passing one at all makes the browser open a popup window, not a tab.
    if (inNewTab) {
      window.open(item.path, '_blank');
      return;
    }

    setQuery('');
    setIsOverlayOpen(false);
    setIsOpen(false);
    inputRef.current?.blur();
    navigate(item.path);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      if (isOverlayOpen) closeOverlay();
      else if (isPanelOpen) setIsOpen(false);
      else inputRef.current?.blur();
      return;
    }
    // Above the empty guard: Tab must still leave the field when there is nothing to switch.
    if (event.key === 'Tab' && isPanelOpen && tabs.length > 1) {
      event.preventDefault();
      const step = event.shiftKey ? -1 : 1;
      const at = tabs.findIndex((tab) => tab.key === activeCategory);
      selectCategory(tabs[(at + step + tabs.length) % tabs.length].key);
      return;
    }
    if (!isPanelOpen || items.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % items.length);
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => (index - 1 + items.length) % items.length);
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const item = items[activeIndex];
      if (item != null) handleSelect(item, opensInNewTab(event));
    }
  };

  const panel = (
    <SearchPanel
      groups={groups}
      tabs={tabs}
      activeCategory={activeCategory}
      onSelectCategory={selectCategory}
      query={debouncedQuery}
      activeIndex={activeIndex}
      isLoading={isLoading}
      hasError={hasError}
      onRetry={retry}
      hasNextPage={hasNextPage}
      onLoadNextPage={loadNextPage}
      onSelect={handleSelect}
      onHover={setActiveIndex}
      listboxId={listboxId}
      fullWidth={compact}
    />
  );

  const searchInput = (
    // A field belonging to no form is pooled into one synthetic form with every other stray
    // input on the page, where no autocomplete hint can reach it. Owning it by a search form
    // that opts out is what gives `autoComplete="off"` below something to bind to.
    <SearchForm autoComplete="off" onSubmit={(event) => event.preventDefault()}>
      <SearchInput
        ref={inputRef}
        value={query}
        placeholder={`${searchScope}...`}
        variant={compact ? 'borderless' : 'outlined'}
        // An unnamed text field is a username candidate for any password form on the page, and
        // Chrome filled this one with the signed-in account's mail on /admin/account. These three
        // state the field's intent but did not stop it on their own; `readOnly` below does.
        type="search"
        name="global-search"
        autoComplete="off"
        role="combobox"
        aria-label={searchScope}
        aria-autocomplete="list"
        aria-expanded={isPanelOpen}
        aria-controls={listboxId}
        aria-activedescendant={activeOptionId}
        prefix={<IconSearch color={RacemapColors.DarkGray} />}
        suffix={!compact && <KeyCap>{isMac ? '⌘K' : 'Ctrl K'}</KeyCap>}
        styles={{ suffix: { marginInlineStart: 6 } }}
        readOnly={isReadOnly}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          setIsReadOnly(false);
          setIsOpen(true);
        }}
        onBlur={() => setIsReadOnly(true)}
        allowClear
      />
    </SearchForm>
  );

  if (compact) {
    return (
      <>
        <Button aria-label="Search" icon={<IconSearch />} type="primary" ghost onClick={open} />
        <SheetModal
          open={isOverlayOpen}
          onCancel={closeOverlay}
          closable={false}
          footer={null}
          destroyOnHidden
          // The dialog mounts its content behind an animation, so an effect on `open` would
          // focus an input that does not exist yet.
          afterOpenChange={(opened) => opened && focusInput()}
          styles={{ wrapper: { padding: 0 }, container: SheetContainerStyle }}
        >
          <SheetField>{searchInput}</SheetField>
          {isPanelOpen && panel}
        </SheetModal>
      </>
    );
  }

  return (
    <DesktopContainer ref={containerRef}>
      {searchInput}
      {isPanelOpen && <PanelAnchor>{panel}</PanelAnchor>}
    </DesktopContainer>
  );
};

// The header bar's only flexing child, so it absorbs the whole squeeze and nothing else wraps.
const DesktopContainer = styled.div`
  position: relative;
  flex: 1 1 auto;
  min-width: 140px;
  max-width: 440px;
`;

// Right-anchored so the panel grows leftwards, landing nearer the middle of the screen.
const PanelAnchor = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 1001;
`;

// Carries no layout of its own: it exists to own the field, so the header keeps sizing the input.
const SearchForm = styled.form`
  display: contents;
`;

// type="search" brings a native clear button that would sit beside antd's allowClear one.
const SearchInput = styled(Input)`
  &::-webkit-search-cancel-button,
  &::-webkit-search-decoration,
  input::-webkit-search-cancel-button,
  input::-webkit-search-decoration {
    display: none;
  }
`;

// One card: the field is its first row and the results share that surface, not a dropdown below it.
const SheetModal = styled(Modal)`
  top: 0;
  margin: 0 !important;
  padding-bottom: 0;
  max-width: none !important;
  width: 100vw !important;
`;

// Passed through Modal's `styles.container` rather than a `.ant-modal-content` rule, which antd 6
// no longer emits.
const SheetContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  maxHeight: '100vh',
  borderRadius: 0,
  padding: 0,
};

const SheetField = styled.div`
  flex: none;
  padding: 4px 6px;
  border-bottom: 1px solid ${RacemapColors.LightGray};
`;
